import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { fetchPublishedFaq, createSupportTicket } from '@quickbee/api-helpdesk';

// src/react/styles.ts
var ASSISTANT_CSS = `.qba-scope {
  --qba-primary: var(--qb-color-primary, oklch(var(--primary-500, 0.42 0.16 18)));
  --qba-primary-hover: var(--qb-color-primary-hover, oklch(var(--primary-600, 0.36 0.15 18)));
  --qba-on-primary: var(--qb-color-text-on-primary, #fff);

  --qba-surface: var(--qb-color-bg-elevated, oklch(var(--background-100, 0.96 0.014 85)));
  --qba-subtle: var(--qb-color-bg-subtle, oklch(var(--background-200, 0.94 0.018 85)));
  --qba-canvas: var(--qb-color-bg-canvas, oklch(var(--background-50, 0.975 0.012 85)));
  --qba-border: var(--qb-color-border-default, oklch(var(--background-300, 0.9 0.022 85)));

  --qba-text: var(--qb-color-text-primary, oklch(var(--foreground-900, 0.16 0.016 260)));
  --qba-text-2: var(--qb-color-text-secondary, oklch(var(--foreground-600, 0.4 0.016 260)));
  --qba-text-3: var(--qb-color-text-tertiary, oklch(var(--foreground-500, 0.5 0.015 260)));

  --qba-error: var(--qb-color-text-error, #b91c1c);
  --qba-success: var(--qb-color-text-success, #15803d);

  --qba-radius: 12px;
  --qba-radius-lg: 16px;
  --qba-shadow: 0 10px 30px rgb(0 0 0 / 18%);
  --qba-focus: 0 0 0 3px color-mix(in oklab, var(--qba-primary) 40%, transparent);

  font-family: inherit;
}

.qba-launcher {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: var(--qb-z-overlay, 300);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 56px: comfortably over the 44px tap-target floor without inflating into the
     content the way the shell's blanket min-height rule does. */
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 999px;
  background: var(--qba-primary);
  color: var(--qba-on-primary);
  box-shadow: var(--qba-shadow);
  cursor: pointer;
  transition: transform 120ms ease, background-color 120ms ease;
}

.qba-launcher:hover {
  background: var(--qba-primary-hover);
  transform: translateY(-1px);
}

.qba-launcher:focus-visible {
  outline: none;
  box-shadow: var(--qba-focus);
}

.qba-launcherMobile {
  /* Clear of the PWA's bottom tab bar, which owns the bottom 64px. */
  bottom: calc(20px + 64px);
}

.qba-panel {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: var(--qb-z-overlay, 300);
  display: flex;
  flex-direction: column;
  width: min(400px, calc(100vw - 32px));
  height: min(560px, calc(100vh - 64px));
  overflow: hidden;
  border: 1px solid var(--qba-border);
  border-radius: var(--qba-radius-lg);
  background: var(--qba-surface);
  box-shadow: var(--qba-shadow);
}

.qba-panelMobile {
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: min(88vh, 100dvh);
  border-right: none;
  border-bottom: none;
  border-left: none;
  border-radius: var(--qba-radius-lg) var(--qba-radius-lg) 0 0;
}

.qba-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid var(--qba-border);
}

.qba-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--qba-text);
}

.qba-iconButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--qba-text-2);
  cursor: pointer;
}

.qba-iconButton:hover {
  background: var(--qba-subtle);
  color: var(--qba-text);
}

.qba-iconButton:focus-visible {
  outline: none;
  box-shadow: var(--qba-focus);
}

.qba-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.qba-bubble {
  max-width: 88%;
  padding: 12px 16px;
  border-radius: var(--qba-radius);
  font-size: 0.875rem;
  line-height: 1.5;
  /* Answers are the article's own words, newlines and all. */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.qba-assistantBubble {
  align-self: flex-start;
  background: var(--qba-subtle);
  color: var(--qba-text);
}

.qba-userBubble {
  align-self: flex-end;
  background: var(--qba-primary);
  color: var(--qba-on-primary);
}

.qba-pending {
  color: var(--qba-text-3);
}

.qba-sources {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-self: flex-start;
  max-width: 88%;
}

.qba-sourceChip {
  padding: 4px 12px;
  border: 1px solid var(--qba-border);
  border-radius: 999px;
  background: var(--qba-surface);
  color: var(--qba-text-2);
  font-size: 0.75rem;
}

.qba-choices {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: flex-start;
  max-width: 88%;
}

.qba-choice,
.qba-secondaryAction {
  padding: 8px 16px;
  border: 1px solid var(--qba-border);
  border-radius: 10px;
  background: var(--qba-surface);
  color: var(--qba-text);
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
}

.qba-choice:hover,
.qba-secondaryAction:hover {
  border-color: var(--qba-text-3);
  background: var(--qba-subtle);
}

.qba-choice:focus-visible,
.qba-secondaryAction:focus-visible {
  outline: none;
  box-shadow: var(--qba-focus);
}

.qba-escalateCard {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--qba-border);
  border-radius: var(--qba-radius);
  background: var(--qba-surface);
}

.qba-fieldLabel {
  display: block;
  margin-bottom: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--qba-text-2);
}

.qba-input,
.qba-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--qba-border);
  border-radius: 10px;
  background: var(--qba-canvas);
  color: var(--qba-text);
  font: inherit;
  font-size: 0.875rem;
}

.qba-textarea {
  min-height: 72px;
  resize: vertical;
}

.qba-input:focus-visible,
.qba-textarea:focus-visible {
  outline: none;
  border-color: var(--qba-primary);
  box-shadow: var(--qba-focus);
}

.qba-hint {
  margin: 0;
  font-size: 0.75rem;
  color: var(--qba-text-3);
}

.qba-error {
  margin: 0;
  font-size: 0.75rem;
  color: var(--qba-error);
}

.qba-primaryAction {
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  background: var(--qba-primary);
  color: var(--qba-on-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
}

.qba-primaryAction:disabled {
  background: var(--qba-subtle);
  color: var(--qba-text-3);
  cursor: not-allowed;
}

.qba-primaryAction:focus-visible {
  outline: none;
  box-shadow: var(--qba-focus);
}

.qba-success {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.qba-successTitle {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--qba-success);
}

.qba-composer {
  display: flex;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--qba-border);
}

.qba-composer .qba-input {
  flex: 1;
}

@media (prefers-reduced-motion: reduce) {
  .qba-launcher {
    transition: none;
  }
  .qba-launcher:hover {
    transform: none;
  }
}`;
var STYLE_ELEMENT_ID = "qb-assistant-styles";
function ensureAssistantStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = ASSISTANT_CSS;
  document.head.appendChild(style);
}
var cx = {
  assistantBubble: "qba-assistantBubble",
  bubble: "qba-bubble",
  choice: "qba-choice",
  choices: "qba-choices",
  composer: "qba-composer",
  error: "qba-error",
  escalateCard: "qba-escalateCard",
  fieldLabel: "qba-fieldLabel",
  header: "qba-header",
  hint: "qba-hint",
  iconButton: "qba-iconButton",
  input: "qba-input",
  launcher: "qba-launcher",
  launcherMobile: "qba-launcherMobile",
  messages: "qba-messages",
  panel: "qba-panel",
  panelMobile: "qba-panelMobile",
  pending: "qba-pending",
  primaryAction: "qba-primaryAction",
  scope: "qba-scope",
  secondaryAction: "qba-secondaryAction",
  sourceChip: "qba-sourceChip",
  sources: "qba-sources",
  success: "qba-success",
  successTitle: "qba-successTitle",
  textarea: "qba-textarea",
  title: "qba-title",
  userBubble: "qba-userBubble"
};
function EscalateCard({
  strings,
  escalation,
  defaultSubject,
  state,
  onSubmit
}) {
  const [subject, setSubject] = useState(defaultSubject);
  const [details, setDetails] = useState("");
  const [seededFrom, setSeededFrom] = useState(defaultSubject);
  useEffect(() => {
    if (defaultSubject === seededFrom) return;
    setSeededFrom(defaultSubject);
    setSubject((current) => current === seededFrom ? defaultSubject : current);
  }, [defaultSubject, seededFrom]);
  if (state.status === "sent") {
    return /* @__PURE__ */ jsx("div", { className: cx.escalateCard, "data-testid": "qb-assistant-ticket-created", children: /* @__PURE__ */ jsxs("div", { className: cx.success, children: [
      /* @__PURE__ */ jsx("span", { className: cx.successTitle, children: strings.ticket.created.replace("{number}", state.ticketNumber) }),
      /* @__PURE__ */ jsx("p", { className: cx.hint, children: strings.ticket.createdBody })
    ] }) });
  }
  const sending = state.status === "sending";
  function handleSubmit(event) {
    event.preventDefault();
    if (sending || !subject.trim()) return;
    onSubmit({ subject, details, escalation });
  }
  return /* @__PURE__ */ jsxs(
    "form",
    {
      className: cx.escalateCard,
      onSubmit: handleSubmit,
      "data-testid": "qb-assistant-ticket-form",
      children: [
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("label", { className: cx.fieldLabel, htmlFor: "qb-assistant-subject", children: strings.ticket.subjectLabel }),
          /* @__PURE__ */ jsx(
            "input",
            {
              id: "qb-assistant-subject",
              className: cx.input,
              value: subject,
              onChange: (event) => setSubject(event.target.value),
              maxLength: 140,
              "data-testid": "qb-assistant-ticket-subject"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("label", { className: cx.fieldLabel, htmlFor: "qb-assistant-details", children: strings.ticket.detailsLabel }),
          /* @__PURE__ */ jsx(
            "textarea",
            {
              id: "qb-assistant-details",
              className: cx.textarea,
              value: details,
              onChange: (event) => setDetails(event.target.value),
              "data-testid": "qb-assistant-ticket-details"
            }
          ),
          /* @__PURE__ */ jsx("p", { className: cx.hint, children: strings.ticket.detailsHint })
        ] }),
        state.status === "failed" ? /* @__PURE__ */ jsx("p", { className: cx.error, role: "alert", children: strings.ticket.failed }) : null,
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "submit",
            className: cx.primaryAction,
            disabled: sending || !subject.trim(),
            "data-testid": "qb-assistant-ticket-submit",
            children: sending ? strings.ticket.submitting : strings.ticket.submit
          }
        )
      ]
    }
  );
}
function AssistantPanel(props) {
  const { strings, messages, thinking, ticket, variant } = props;
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, ticket]);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  function submit(event) {
    event.preventDefault();
    const question = draft.trim();
    if (!question || thinking) return;
    setDraft("");
    props.onAsk(question);
  }
  const lastUserQuestion = [...messages].reverse().find((message) => message.role === "user")?.text ?? "";
  const liveEscalationId = [...messages].reverse().find((message) => message.escalation)?.id ?? null;
  return /* @__PURE__ */ jsxs(
    "section",
    {
      className: [cx.scope, cx.panel, variant === "mobile" ? cx.panelMobile : ""].filter(Boolean).join(" "),
      role: "dialog",
      "aria-modal": "false",
      "aria-label": strings.panelTitle,
      "data-testid": "qb-assistant-panel",
      children: [
        /* @__PURE__ */ jsxs("header", { className: cx.header, children: [
          /* @__PURE__ */ jsx("h2", { className: cx.title, children: strings.panelTitle }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: cx.iconButton,
              onClick: props.onClose,
              "aria-label": strings.close,
              "data-testid": "qb-assistant-panel-close",
              children: /* @__PURE__ */ jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M3 3l10 10M13 3L3 13",
                  stroke: "currentColor",
                  strokeWidth: "1.75",
                  strokeLinecap: "round"
                }
              ) })
            }
          )
        ] }),
        /* @__PURE__ */ jsx("div", { className: cx.messages, ref: listRef, "data-testid": "qb-assistant-messages", children: messages.map((message) => /* @__PURE__ */ jsx(
          Fragmented,
          {
            message,
            strings,
            ticket,
            defaultSubject: lastUserQuestion,
            escalationLive: message.id === liveEscalationId,
            onAsk: props.onAsk,
            onRequestHuman: props.onRequestHuman,
            ...props.onNavigate ? { onNavigate: props.onNavigate } : {},
            onSubmitTicket: props.onSubmitTicket
          },
          message.id
        )) }),
        /* @__PURE__ */ jsxs("form", { className: cx.composer, onSubmit: submit, children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              ref: inputRef,
              className: cx.input,
              value: draft,
              onChange: (event) => setDraft(event.target.value),
              placeholder: strings.composerPlaceholder,
              "aria-label": strings.composerPlaceholder,
              disabled: thinking,
              "data-testid": "qb-assistant-composer-input"
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "submit",
              className: cx.primaryAction,
              disabled: thinking || !draft.trim(),
              "data-testid": "qb-assistant-composer-send",
              children: strings.send
            }
          )
        ] })
      ]
    }
  );
}
function Fragmented({
  message,
  strings,
  ticket,
  defaultSubject,
  onAsk,
  onRequestHuman,
  onNavigate,
  onSubmitTicket,
  escalationLive
}) {
  const isUser = message.role === "user";
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: [
          cx.bubble,
          isUser ? cx.userBubble : cx.assistantBubble,
          message.pending ? cx.pending : ""
        ].filter(Boolean).join(" "),
        "data-testid": isUser ? "qb-assistant-message-user" : "qb-assistant-message-reply",
        children: message.text
      }
    ),
    message.suggestions?.length ? /* @__PURE__ */ jsx("div", { className: cx.choices, children: message.suggestions.map((suggestion) => /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: cx.choice,
        onClick: () => onAsk(suggestion.title),
        "data-testid": `qb-assistant-choice-${suggestion.articleId}`,
        children: suggestion.title
      },
      suggestion.articleId
    )) }) : null,
    message.route && onNavigate ? /* @__PURE__ */ jsx("div", { className: cx.choices, children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: cx.choice,
        onClick: () => onNavigate(message.route.path),
        "data-testid": "qb-assistant-route",
        children: message.route.label
      }
    ) }) : null,
    message.passages?.length ? /* @__PURE__ */ jsxs("div", { className: cx.sources, children: [
      /* @__PURE__ */ jsx("span", { className: cx.sourceChip, children: strings.sourceLabel }),
      message.passages.map((passage) => /* @__PURE__ */ jsx("span", { className: cx.sourceChip, children: passage.title }, passage.articleId))
    ] }) : null,
    !isUser && message.passages?.length && !message.escalation ? /* @__PURE__ */ jsx("div", { className: cx.choices, children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: cx.secondaryAction,
        onClick: onRequestHuman,
        "data-testid": "qb-assistant-talk-to-person",
        children: strings.talkToPerson
      }
    ) }) : null,
    message.escalation && escalationLive ? /* @__PURE__ */ jsx(
      EscalateCard,
      {
        strings,
        escalation: message.escalation,
        defaultSubject,
        state: ticket,
        onSubmit: onSubmitTicket
      }
    ) : null
  ] });
}

// src/engine/intent.ts
var FIRST_PERSON = /\b(my|mine|me|i|i'm|im|we|our)\b|मेरा|मेरी|मेरे|मुझे|हम|எனது|என்|எனக்கு|நான்/iu;
var HOW_TO = /^\s*(?:please\s+)?(?:can you\s+)?(?:tell me\s+)?(?:how (?:do|can|would|should|does) (?:i|we|it|you)|how to|what (?:is|are) the (?:process|procedure|steps?|rules?)|where (?:do|can) i (?:find|see))\b/iu;
var RULES = [
  {
    code: "REGULATORY",
    ignoresHowTo: true,
    patterns: [
      /\b(fraud|fraudulent|scam|scammed|cheat(?:ed|ing)?|stole[n]?|theft|police|fir\b|court|legal action|lawyer|ombudsman|rbi\b|sebi\b|regulator|grievance|complain(?:t|ts)?)\b/iu,
      /धोखा|शिकायत|पुलिस/u,
      /மோசடி|புகார்|காவல்/u
    ]
  },
  {
    code: "MONEY_MOVEMENT",
    patterns: [
      /\b(refund|charge ?back|reversal|double (?:charge|debit|payment)|debited twice|charged twice|not credited|didn'?t (?:get|receive)|money (?:is )?missing|payment failed|stuck)\b/iu,
      /\b(where is my|haven'?t (?:got|received)) (?:money|payout|refund|gold|payment|amount)\b/iu,
      /पैसे नहीं|वापसी/u,
      /பணம் வரவில்லை|திரும்ப/u
    ]
  },
  {
    code: "DESTRUCTIVE",
    patterns: [
      /\b(close|delete|cancel|terminate|withdraw from|opt out of|stop) (?:my|our) (?:account|scheme|plan|subscription|enrolment|enrollment|sip|membership)\b/iu,
      /\bi (?:want|need|wish) to (?:close|delete|cancel|quit|leave)\b/iu
    ]
  },
  {
    code: "ACCOUNT_SPECIFIC",
    patterns: [
      /\b(?:what(?:'s| is)|where(?:'s| is)|how much|when (?:is|will|did)|show me|check) .{0,24}\b(?:my|our)\b/iu,
      /\bmy (?:balance|gold|grams?|holdings?|portfolio|payout|maturity|kyc|status|ticket|scheme|plan|instal?ment|due date|statement|invoice|order|redemption|loan|limit)\b/iu,
      /\bmy (?:account|profile) (?:is|was|has|shows)\b/iu,
      /मेरा (?:बैलेंस|सोना|खाता|स्टेटस)/u,
      /எனது (?:இருப்பு|தங்கம்|கணக்கு)/u
    ]
  }
];
function classifyIntent(question) {
  const text = question.trim();
  if (!text) return null;
  const firstPerson = FIRST_PERSON.test(text);
  const howTo = HOW_TO.test(text);
  for (const rule of RULES) {
    if (!firstPerson) continue;
    if (howTo && !rule.ignoresHowTo) continue;
    if (rule.patterns.some((pattern) => pattern.test(text))) {
      return { code: rule.code };
    }
  }
  return null;
}
function priorityForIntent(code) {
  switch (code) {
    case "REGULATORY":
      return "CRITICAL";
    case "MONEY_MOVEMENT":
      return "HIGH";
    default:
      return "MEDIUM";
  }
}

// src/engine/smalltalk.ts
var TRIM = /^[\s\p{P}\p{S}\p{Extended_Pictographic}]+|[\s\p{P}\p{S}\p{Extended_Pictographic}]+$/gu;
var RULES2 = [
  {
    code: "GREETING",
    pattern: /^(?:hi+|hey+|hello+|helo|hiya|yo|heya|hi there|hey there|hello there|good (?:morning|afternoon|evening|day)|greetings|namaste|namaskar|vanakkam|salaam|assalam(?:u)? alaikum|नमस्ते|नमस्कार|हाय|हेलो|வணக்கம்|ஹாய்)$/u
  },
  {
    code: "THANKS",
    pattern: /^(?:thanks?|thank you|thanks a lot|thanks so much|thank you so much|thanku|thankyou|ty|thx|tysm|cheers|much appreciated|appreciate it|great thanks|ok thanks|okay thanks|perfect thanks|धन्यवाद|शुक्रिया|थैंक्स|நன்றி)$/u
  },
  {
    code: "GOODBYE",
    pattern: /^(?:bye+|goodbye|good bye|see you|see ya|cya|take care|later|gtg|good night|अलविदा|फिर मिलेंगे|போய் வருகிறேன்|பை)$/u
  },
  {
    code: "HOW_ARE_YOU",
    pattern: /^(?:how are you(?: doing)?|how r u|hows it going|how'?s it going|how do you do|you ok|are you ok|whats up|what'?s up|sup|कैसे हो|कैसी हो|எப்படி இருக்கிறீர்கள்)$/u
  },
  {
    code: "IDENTITY",
    pattern: /^(?:who are you|what are you|who is this|are you (?:a )?(?:bot|robot|human|real|a real person|an? ai|ai)|are you human|am i talking to a (?:bot|human|person)|तुम कौन हो|आप कौन हैं|நீங்கள் யார்)$/u
  },
  {
    code: "HELP_OFFER",
    pattern: /^(?:help|help me|i need help|need help|can you help(?: me)?|please help|support|i have a question|question|मदद|मदद चाहिए|உதவி)$/u
  },
  {
    code: "AFFIRM",
    pattern: /^(?:ok+|okay|k|kk|sure|yes|yeah|yep|yup|right|alright|all right|fine|cool|got it|understood|noted|nice|great|good|hmm+|acha|theek hai|ठीक है|अच्छा|சரி)$/u
  }
];
function classifySmallTalk(question) {
  const trimmed = question.trim().toLowerCase().replace(TRIM, "");
  if (!trimmed) return "UNCLEAR";
  for (const rule of RULES2) {
    if (rule.pattern.test(trimmed)) return rule.code;
  }
  return null;
}

// src/engine/tokenize.ts
var STOPWORDS = /* @__PURE__ */ new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "but",
  "by",
  "can",
  "do",
  "does",
  "for",
  "from",
  "how",
  "i",
  "if",
  "in",
  "is",
  "it",
  "me",
  "my",
  "of",
  "on",
  "or",
  "that",
  "the",
  "their",
  "them",
  "they",
  "this",
  "to",
  "was",
  "what",
  "when",
  "where",
  "which",
  "who",
  "will",
  "with",
  "you",
  "your"
]);
var WORD = /[\p{L}\p{N}\p{M}\u200c\u200d]+(?:['’\-][\p{L}\p{N}\p{M}\u200c\u200d]+)*/gu;
function tokenize(text) {
  const matches = text.toLowerCase().match(WORD);
  if (!matches) return [];
  const out = [];
  for (const token of matches) {
    if (token.length === 1 && !/[\p{N}]/u.test(token) && /\p{Script=Latin}/u.test(token)) continue;
    if (STOPWORDS.has(token)) continue;
    out.push(token);
  }
  return out;
}
function tokenizeRaw(text) {
  return text.toLowerCase().match(WORD) ?? [];
}

// src/engine/bm25.ts
var K1 = 1.2;
var B = 0.75;
function buildBm25Index(searchTexts) {
  const postings = /* @__PURE__ */ new Map();
  const lengths = new Int32Array(searchTexts.length);
  let total = 0;
  for (let i = 0; i < searchTexts.length; i++) {
    const tokens = tokenize(searchTexts[i]);
    lengths[i] = tokens.length;
    total += tokens.length;
    for (const token of tokens) {
      let posting = postings.get(token);
      if (!posting) {
        posting = /* @__PURE__ */ new Map();
        postings.set(token, posting);
      }
      posting.set(i, (posting.get(i) ?? 0) + 1);
    }
  }
  return {
    postings,
    lengths,
    averageLength: searchTexts.length ? total / searchTexts.length : 0,
    chunkCount: searchTexts.length
  };
}
function scoreBm25(index, query, limit = 20) {
  const terms = tokenize(query);
  if (!terms.length || !index.chunkCount) return [];
  const scores = /* @__PURE__ */ new Map();
  for (const term of terms) {
    const posting = index.postings.get(term);
    if (!posting) continue;
    const df = posting.size;
    const idf = Math.max(
      Math.log(1 + (index.chunkCount - df + 0.5) / (df + 0.5)),
      1e-6
    );
    for (const [chunk, tf] of posting) {
      const length = index.lengths[chunk] ?? 0;
      const norm = index.averageLength ? 1 - B + B * length / index.averageLength : 1;
      const contribution = idf * (tf * (K1 + 1)) / (tf + K1 * norm);
      scores.set(chunk, (scores.get(chunk) ?? 0) + contribution);
    }
  }
  return [...scores.entries()].map(([chunk, score]) => ({ chunk, score })).sort((a, b) => b.score - a.score).slice(0, limit);
}
var SATURATION = 12;
function normaliseBm25(score) {
  return score <= 0 ? 0 : score / (score + SATURATION);
}

// src/engine/chunk.ts
var TARGET = 700;
var OVERLAP = 120;
var MIN_TAIL = 160;
var SENTENCE_END = /(?<=[.!?।॥])\s+/u;
function chunkSearchText(chunk) {
  return `${chunk.title}
${chunk.text}`;
}
function chunkArticle(article) {
  const title = article.title.trim();
  const body = article.body.replace(/\s+/g, " ").trim();
  if (!body) return title ? [{ title, text: title }] : [];
  if (body.length <= TARGET) return [{ title, text: body }];
  const sentences = body.split(SENTENCE_END).filter(Boolean);
  const passages = [];
  let current = "";
  for (const sentence of sentences) {
    if (!current && sentence.length > TARGET) {
      passages.push(sentence);
      continue;
    }
    if (current && current.length + sentence.length + 1 > TARGET) {
      passages.push(current);
      current = tailOf(current, OVERLAP);
    }
    current = current ? `${current} ${sentence}` : sentence;
  }
  if (current) passages.push(current);
  if (passages.length > 1) {
    const last = passages[passages.length - 1];
    if (last.length < MIN_TAIL) {
      const previous = passages[passages.length - 2];
      passages.splice(passages.length - 2, 2, `${previous} ${last}`);
    }
  }
  return passages.map((text) => ({ title, text }));
}
function tailOf(text, chars) {
  if (text.length <= chars) return text;
  const slice = text.slice(text.length - chars);
  const space = slice.indexOf(" ");
  return space === -1 ? slice : slice.slice(space + 1);
}

// src/engine/fuse.ts
var RRF_K = 60;
var SURFACE_BOOST = 1.25;
var WILDCARD_SURFACE = "all";
function fuse(input) {
  const { lexical, semantic, surfaceOf, activeSurface, limit = 8 } = input;
  const rank = /* @__PURE__ */ new Map();
  const confidence = /* @__PURE__ */ new Map();
  const contribute = (chunk, position, normalised) => {
    rank.set(chunk, (rank.get(chunk) ?? 0) + 1 / (RRF_K + position + 1));
    confidence.set(chunk, Math.max(confidence.get(chunk) ?? 0, normalised));
  };
  lexical.forEach((hit, position) => {
    contribute(hit.chunk, position, normaliseBm25(hit.score));
  });
  semantic?.forEach((hit, position) => {
    contribute(hit.chunk, position, hit.score);
  });
  const out = [];
  for (const [chunk, rrf] of rank) {
    const surface = surfaceOf(chunk);
    const boosted = activeSurface && (surface === activeSurface || surface === WILDCARD_SURFACE) ? rrf * SURFACE_BOOST : rrf;
    out.push({ chunk, rank: boosted, confidence: confidence.get(chunk) ?? 0 });
  }
  return out.sort((a, b) => b.rank - a.rank || b.confidence - a.confidence).slice(0, limit);
}

// src/engine/vectors.ts
function quantiseInt8(vector) {
  let max = 0;
  for (let i = 0; i < vector.length; i++) {
    const value = Math.abs(vector[i]);
    if (value > max) max = value;
  }
  const scale = max === 0 ? 1 : max / 127;
  const data = new Int8Array(vector.length);
  for (let i = 0; i < vector.length; i++) {
    const q = Math.round(vector[i] / scale);
    data[i] = q > 127 ? 127 : q < -127 ? -127 : q;
  }
  return { data, scale };
}
function packVectors(vectors, dim) {
  const count = vectors.length;
  const buffer = new ArrayBuffer(4 * count + count * dim);
  const scales = new Float32Array(buffer, 0, count);
  const data = new Int8Array(buffer, 4 * count, count * dim);
  for (let i = 0; i < count; i++) {
    const vector = vectors[i];
    if (vector.length !== dim) {
      throw new Error(`vector ${i} has dim ${vector.length}, expected ${dim}`);
    }
    const { data: quantised, scale } = quantiseInt8(vector);
    scales[i] = scale;
    data.set(quantised, i * dim);
  }
  return new Uint8Array(buffer);
}
function unpackVectors(bytes, dim) {
  const stride = 4 + dim;
  if (bytes.byteLength % stride !== 0) {
    throw new Error(
      `vector sidecar is ${bytes.byteLength} bytes, not a multiple of ${stride} (dim ${dim})`
    );
  }
  const count = bytes.byteLength / stride;
  const aligned = bytes.byteOffset % 4 === 0 ? bytes : new Uint8Array(bytes.slice());
  const scales = new Float32Array(aligned.buffer, aligned.byteOffset, count);
  const data = new Int8Array(aligned.buffer, aligned.byteOffset + 4 * count, count * dim);
  const out = new Float32Array(count * dim);
  for (let i = 0; i < count; i++) {
    const scale = scales[i];
    const base = i * dim;
    let sumSquares = 0;
    for (let d = 0; d < dim; d++) {
      const value = data[base + d] * scale;
      out[base + d] = value;
      sumSquares += value * value;
    }
    const norm = Math.sqrt(sumSquares);
    if (norm > 0) {
      for (let d = 0; d < dim; d++) out[base + d] = out[base + d] / norm;
    }
  }
  return out;
}
function normalise(vector) {
  let sumSquares = 0;
  for (let i = 0; i < vector.length; i++) {
    const value = vector[i];
    sumSquares += value * value;
  }
  const norm = Math.sqrt(sumSquares);
  if (norm > 0) {
    for (let i = 0; i < vector.length; i++) vector[i] = vector[i] / norm;
  }
  return vector;
}
function scoreVectors(query, vectors, dim, chunkOf, limit = 20) {
  const count = vectors.length / dim;
  const hits = [];
  for (let i = 0; i < count; i++) {
    const base = i * dim;
    let dot = 0;
    for (let d = 0; d < dim; d++) {
      dot += query[d] * vectors[base + d];
    }
    hits.push({ chunk: chunkOf[i], score: dot < 0 ? 0 : dot > 1 ? 1 : dot });
  }
  return hits.sort((a, b) => b.score - a.score).slice(0, limit);
}

// src/engine/retrieval.ts
var TOP_K = 4;
var CANDIDATES = 20;
var Retriever = class {
  corpus;
  bm25;
  banks = [];
  constructor(corpus) {
    this.corpus = corpus;
    this.bm25 = buildBm25Index(
      corpus.bundle.chunks.map((chunk) => chunkSearchText({ title: chunk.t, text: chunk.x }))
    );
    if (corpus.vectors && corpus.dim > 0) {
      const chunkOf = [];
      corpus.bundle.chunks.forEach((chunk, index) => {
        if (chunk.v >= 0) chunkOf[chunk.v] = index;
      });
      this.banks.push({ vectors: corpus.vectors, chunkOf });
    }
  }
  /** Chunks with no vector yet — what the semantic tier embeds on warm. */
  unvectorisedChunks() {
    const out = [];
    this.corpus.bundle.chunks.forEach((chunk, index) => {
      if (chunk.v < 0) {
        out.push({ index, text: chunkSearchText({ title: chunk.t, text: chunk.x }) });
      }
    });
    return out;
  }
  addBank(bank) {
    if (bank.chunkOf.length) this.banks.push(bank);
  }
  get hasVectors() {
    return this.banks.length > 0;
  }
  get chunkCount() {
    return this.corpus.bundle.chunks.length;
  }
  passageAt(chunkIndex, score) {
    const chunk = this.corpus.bundle.chunks[chunkIndex];
    if (!chunk) return null;
    const article = this.corpus.bundle.articles[chunk.a];
    if (!article) return null;
    return {
      articleId: article.id,
      title: chunk.t,
      text: chunk.x,
      surfaceKey: article.surfaceKey,
      score
    };
  }
  /**
   * Runs the pipeline. `queryVector` must already be L2-normalised; pass `null` on
   * the lexical tier.
   */
  search(question, activeSurface, queryVector) {
    const lexical = scoreBm25(this.bm25, question, CANDIDATES);
    let semantic = null;
    if (queryVector && this.banks.length) {
      const all = [];
      for (const bank of this.banks) {
        all.push(
          ...scoreVectors(
            queryVector,
            bank.vectors,
            this.corpus.dim,
            bank.chunkOf,
            CANDIDATES
          )
        );
      }
      semantic = all.sort((a, b) => b.score - a.score).slice(0, CANDIDATES);
    }
    const fused = fuse({
      lexical,
      semantic,
      surfaceOf: (chunk) => {
        const target = this.corpus.bundle.chunks[chunk];
        return target ? this.corpus.bundle.articles[target.a]?.surfaceKey ?? "" : "";
      },
      activeSurface,
      limit: TOP_K
    });
    const passages = [];
    for (const hit of fused) {
      const passage = this.passageAt(hit.chunk, hit.confidence);
      if (passage) passages.push(passage);
    }
    return passages;
  }
};

// src/engine/verdict.ts
var BANDS = {
  /**
   * **Measured**, by `node scripts/assistant/calibrate.mjs` against the real
   * 101-article corpus. On that probe set the weakest correct top-1 answer scores
   * 0.418 and the strongest false positive — "who won the cricket match", which
   * shares "match" with a PEP-screening article — scores 0.284. The floors sit
   * just inside that gap.
   *
   * These started life as 0.5 / 0.3, chosen by eye, which sent seven of the eight
   * answerable probes to the clarify band: correct, top-1 answers rendered as
   * "did you mean?". Re-run the script after any corpus change; do not nudge these
   * by hand.
   */
  lexical: { answer: 0.39, clarify: 0.31 },
  /**
   * **Measured**, reproducibly:
   *
   *     scripts/assistant/build-bundle-docker.sh --calibrate
   *
   * (containerised because the embedder needs an ONNX runtime that does not load
   * on every developer machine — see `build-bundle.mjs`). Cosine is shaped quite
   * differently from normalised BM25, so the lexical numbers could not be reused:
   *
   * | | cosine |
   * |---|---|
   * | direct question, correct top-1 | 0.55 – 0.88 |
   * | plausible question the corpus does not answer, **wrong** article matched | 0.27 – 0.31 |
   * | unrelated question | 0.08 – 0.21 |
   *
   * The floors sit inside the 0.32–0.55 gap. Note the middle row: cosine never
   * approaches zero, so the *clarify* floor is doing real work here in a way it
   * does not on the lexical tier — "i cant get into my account" scores 0.306
   * against an article about contacting an account manager, and 0.33 is what stops
   * that being read back as an answer.
   *
   * These were 0.62 / 0.38, chosen from the literature, which would have refused
   * "what payment methods are accepted" — a direct, correct, top-1 match at 0.556.
   * The same mistake the lexical bands made, in the same direction.
   */
  semantic: { answer: 0.53, clarify: 0.33 },
  generative: { answer: 0.53, clarify: 0.33 }
};
var CLARIFY_SUGGESTIONS = 2;
function verdictFor(tier, passages) {
  const bands = BANDS[tier];
  const top = passages[0];
  if (!top || top.score < bands.clarify) {
    return { status: "escalate", reason: "NO_MATCH" };
  }
  if (top.score < bands.answer) {
    const seen = /* @__PURE__ */ new Set();
    const suggestions = [];
    for (const passage of passages) {
      if (seen.has(passage.articleId)) continue;
      seen.add(passage.articleId);
      suggestions.push({ articleId: passage.articleId, title: passage.title });
      if (suggestions.length === CLARIFY_SUGGESTIONS) break;
    }
    if (suggestions.length < 2) return { status: "escalate", reason: "NO_MATCH" };
    return { status: "clarify", suggestions };
  }
  return { status: "answered", confidence: top.score };
}

// src/i18n/strings.ts
var en = {
  launcherLabel: "Get help",
  panelTitle: "Help",
  composerPlaceholder: "Ask a question\u2026",
  send: "Send",
  close: "Close",
  greeting: "Hi {name} \u{1F44B} I'm the {app} assistant. Ask me about {surface} \u2014 or anything else here.",
  greetingNoName: "Hi \u{1F44B} I'm the {app} assistant. Ask me about {surface} \u2014 or anything else here.",
  answeredLead: "Here's what I have:",
  clarifyLead: "I found a couple of things that might be it \u2014 which one?",
  helpful: "That helped",
  notHelpful: "Not quite",
  talkToPerson: "Talk to a person",
  thinking: "Looking\u2026",
  sourceLabel: "From",
  localeFallback: "I only have English answers for now, so here's the English one.",
  unavailable: "I can't reach my help content right now. You can still raise a ticket and someone will pick it up.",
  smallTalk: {
    GREETING: "Hello! \u{1F44B} How can I help?",
    THANKS: "Happy to help. Anything else I can look up?",
    GOODBYE: "Take care! I'm here whenever you need me.",
    AFFIRM: "Anything else I can help with?",
    HOW_ARE_YOU: "All good, thanks for asking! What can I help you with?",
    IDENTITY: "I'm the {app} assistant \u2014 an automated helper that answers from {app}'s help articles. Anything I can't answer, I'll put you through to a person. What do you need?",
    HELP_OFFER: "Of course \u2014 what do you need help with? Ask me anything in your own words.",
    UNCLEAR: "Sorry, I didn't catch that. What can I help you with?"
  },
  escalate: {
    NO_MATCH: "I don't have a good answer for that one. Let me get a person on it.",
    ACCOUNT_SPECIFIC: "That one's about your own account, and I can't see your details from here. A person can.",
    MONEY_MOVEMENT: "Anything to do with money that's moved \u2014 or hasn't \u2014 should go straight to a person. Let's do that.",
    REGULATORY: "This needs a person, right away. I'm raising it as urgent.",
    DESTRUCTIVE: "That's a big change, and I'd rather a person walked you through it.",
    USER_ASKED: "Of course \u2014 let's get a person on it.",
    ENGINE_UNAVAILABLE: "I can't look anything up at the moment. A person can help in the meantime."
  },
  ticket: {
    heading: "Raise a ticket",
    subjectLabel: "What is it about?",
    detailsLabel: "Anything else that helps?",
    detailsHint: "Your conversation so far is attached, so you don't have to repeat it.",
    submit: "Send to support",
    submitting: "Sending\u2026",
    created: "Ticket {number} is open",
    createdBody: "Someone will pick it up. You'll get an update on your registered number.",
    failed: "That didn't send. Try once more?"
  }
};
var hi = {
  launcherLabel: "\u0938\u0939\u093E\u092F\u0924\u093E \u0932\u0947\u0902",
  panelTitle: "\u0938\u0939\u093E\u092F\u0924\u093E",
  composerPlaceholder: "\u0915\u094B\u0908 \u0938\u0935\u093E\u0932 \u092A\u0942\u091B\u0947\u0902\u2026",
  send: "\u092D\u0947\u091C\u0947\u0902",
  close: "\u092C\u0902\u0926 \u0915\u0930\u0947\u0902",
  greeting: "\u0928\u092E\u0938\u094D\u0924\u0947 {name} \u{1F44B} \u092E\u0948\u0902 {app} \u0938\u0939\u093E\u092F\u0915 \u0939\u0942\u0901\u0964 {surface} \u0915\u0947 \u092C\u093E\u0930\u0947 \u092E\u0947\u0902 \u092A\u0942\u091B\u0947\u0902 \u2014 \u092F\u093E \u092F\u0939\u093E\u0901 \u0915\u093E \u0915\u0941\u091B \u092D\u0940\u0964",
  greetingNoName: "\u0928\u092E\u0938\u094D\u0924\u0947 \u{1F44B} \u092E\u0948\u0902 {app} \u0938\u0939\u093E\u092F\u0915 \u0939\u0942\u0901\u0964 {surface} \u0915\u0947 \u092C\u093E\u0930\u0947 \u092E\u0947\u0902 \u092A\u0942\u091B\u0947\u0902 \u2014 \u092F\u093E \u092F\u0939\u093E\u0901 \u0915\u093E \u0915\u0941\u091B \u092D\u0940\u0964",
  answeredLead: "\u092F\u0939 \u092E\u0947\u0930\u0947 \u092A\u093E\u0938 \u0939\u0948:",
  clarifyLead: "\u0926\u094B \u091A\u0940\u091C\u093C\u0947\u0902 \u092E\u093F\u0932\u0940\u0902 \u091C\u094B \u0936\u093E\u092F\u0926 \u092F\u0939\u0940 \u0939\u094B\u0902 \u2014 \u0915\u094C\u0928 \u0938\u0940?",
  helpful: "\u0907\u0938\u0938\u0947 \u092E\u0926\u0926 \u092E\u093F\u0932\u0940",
  notHelpful: "\u092C\u093F\u0932\u094D\u0915\u0941\u0932 \u0928\u0939\u0940\u0902",
  talkToPerson: "\u0915\u093F\u0938\u0940 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u0938\u0947 \u092C\u093E\u0924 \u0915\u0930\u0947\u0902",
  thinking: "\u0926\u0947\u0916 \u0930\u0939\u093E \u0939\u0942\u0901\u2026",
  sourceLabel: "\u0938\u094D\u0930\u094B\u0924",
  localeFallback: "\u0905\u092D\u0940 \u092E\u0947\u0930\u0947 \u092A\u093E\u0938 \u0915\u0947\u0935\u0932 \u0905\u0902\u0917\u094D\u0930\u0947\u091C\u093C\u0940 \u0909\u0924\u094D\u0924\u0930 \u0939\u0948\u0902, \u0907\u0938\u0932\u093F\u090F \u092F\u0939 \u0905\u0902\u0917\u094D\u0930\u0947\u091C\u093C\u0940 \u092E\u0947\u0902 \u0939\u0948\u0964",
  unavailable: "\u0905\u092D\u0940 \u092E\u0948\u0902 \u0938\u0939\u093E\u092F\u0924\u093E \u0938\u093E\u092E\u0917\u094D\u0930\u0940 \u0924\u0915 \u0928\u0939\u0940\u0902 \u092A\u0939\u0941\u0901\u091A \u092A\u093E \u0930\u0939\u093E \u0939\u0942\u0901\u0964 \u0906\u092A \u091F\u093F\u0915\u091F \u092C\u0928\u093E \u0938\u0915\u0924\u0947 \u0939\u0948\u0902, \u0915\u094B\u0908 \u0907\u0938\u0947 \u0926\u0947\u0916 \u0932\u0947\u0917\u093E\u0964",
  smallTalk: {
    GREETING: "\u0928\u092E\u0938\u094D\u0924\u0947! \u{1F44B} \u092E\u0948\u0902 \u0906\u092A\u0915\u0940 \u0915\u094D\u092F\u093E \u092E\u0926\u0926 \u0915\u0930 \u0938\u0915\u0924\u093E \u0939\u0942\u0901?",
    THANKS: "\u0916\u0941\u0936\u0940 \u0939\u0941\u0908\u0964 \u0914\u0930 \u0915\u0941\u091B \u0926\u0947\u0916\u0942\u0901?",
    GOODBYE: "\u0927\u094D\u092F\u093E\u0928 \u0930\u0916\u093F\u090F! \u095B\u0930\u0942\u0930\u0924 \u0939\u094B \u0924\u094B \u092E\u0948\u0902 \u092F\u0939\u0940\u0902 \u0939\u0942\u0901\u0964",
    AFFIRM: "\u0914\u0930 \u0915\u0941\u091B \u091C\u093F\u0938\u092E\u0947\u0902 \u092E\u0926\u0926 \u0915\u0930\u0942\u0901?",
    HOW_ARE_YOU: "\u0938\u092C \u092C\u0922\u093C\u093F\u092F\u093E! \u092C\u0924\u093E\u0907\u090F, \u0915\u094D\u092F\u093E \u092E\u0926\u0926 \u0915\u0930\u0942\u0901?",
    IDENTITY: "\u092E\u0948\u0902 {app} \u0938\u0939\u093E\u092F\u0915 \u0939\u0942\u0901 \u2014 \u090F\u0915 \u0938\u094D\u0935\u091A\u093E\u0932\u093F\u0924 \u0938\u0939\u093E\u092F\u0915 \u091C\u094B {app} \u0915\u0947 \u0938\u0939\u093E\u092F\u0924\u093E \u0932\u0947\u0916\u094B\u0902 \u0938\u0947 \u091C\u0935\u093E\u092C \u0926\u0947\u0924\u093E \u0939\u0948\u0964 \u091C\u094B \u092E\u0948\u0902 \u0928 \u092C\u0924\u093E \u0938\u0915\u0942\u0901, \u0909\u0938\u0915\u0947 \u0932\u093F\u090F \u0915\u093F\u0938\u0940 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u0938\u0947 \u091C\u094B\u0921\u093C \u0926\u0942\u0901\u0917\u093E\u0964",
    HELP_OFFER: "\u095B\u0930\u0942\u0930 \u2014 \u0915\u093F\u0938 \u092C\u093E\u0930\u0947 \u092E\u0947\u0902 \u092E\u0926\u0926 \u091A\u093E\u0939\u093F\u090F? \u0905\u092A\u0928\u0947 \u0936\u092C\u094D\u0926\u094B\u0902 \u092E\u0947\u0902 \u092A\u0942\u091B\u093F\u090F\u0964",
    UNCLEAR: "\u092E\u093E\u095E \u0915\u0940\u091C\u093F\u090F, \u0938\u092E\u091D \u0928\u0939\u0940\u0902 \u0906\u092F\u093E\u0964 \u092E\u0948\u0902 \u0915\u094D\u092F\u093E \u092E\u0926\u0926 \u0915\u0930 \u0938\u0915\u0924\u093E \u0939\u0942\u0901?"
  },
  escalate: {
    NO_MATCH: "\u0907\u0938\u0915\u093E \u0905\u091A\u094D\u091B\u093E \u0909\u0924\u094D\u0924\u0930 \u092E\u0947\u0930\u0947 \u092A\u093E\u0938 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964 \u092E\u0948\u0902 \u0915\u093F\u0938\u0940 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u0915\u094B \u091C\u094B\u0921\u093C\u0924\u093E \u0939\u0942\u0901\u0964",
    ACCOUNT_SPECIFIC: "\u092F\u0939 \u0906\u092A\u0915\u0947 \u0905\u092A\u0928\u0947 \u0916\u093E\u0924\u0947 \u0938\u0947 \u091C\u0941\u0921\u093C\u093E \u0939\u0948, \u0914\u0930 \u092F\u0939\u093E\u0901 \u0938\u0947 \u092E\u0941\u091D\u0947 \u0906\u092A\u0915\u093E \u0935\u093F\u0935\u0930\u0923 \u0928\u0939\u0940\u0902 \u0926\u093F\u0916\u0924\u093E\u0964 \u0915\u094B\u0908 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u0926\u0947\u0916 \u0938\u0915\u0924\u093E \u0939\u0948\u0964",
    MONEY_MOVEMENT: "\u092A\u0948\u0938\u0947 \u0938\u0947 \u091C\u0941\u0921\u093C\u0940 \u0915\u094B\u0908 \u092D\u0940 \u092C\u093E\u0924 \u0938\u0940\u0927\u0947 \u0915\u093F\u0938\u0940 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u0915\u0947 \u092A\u093E\u0938 \u091C\u093E\u0928\u0940 \u091A\u093E\u0939\u093F\u090F\u0964 \u091A\u0932\u093F\u090F \u0935\u0939\u0940 \u0915\u0930\u0924\u0947 \u0939\u0948\u0902\u0964",
    REGULATORY: "\u0907\u0938\u0915\u0947 \u0932\u093F\u090F \u0924\u0941\u0930\u0902\u0924 \u0915\u093F\u0938\u0940 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u0915\u0940 \u091C\u093C\u0930\u0942\u0930\u0924 \u0939\u0948\u0964 \u092E\u0948\u0902 \u0907\u0938\u0947 \u0905\u0924\u094D\u092F\u093E\u0935\u0936\u094D\u092F\u0915 \u0915\u0947 \u0930\u0942\u092A \u092E\u0947\u0902 \u092D\u0947\u091C \u0930\u0939\u093E \u0939\u0942\u0901\u0964",
    DESTRUCTIVE: "\u092F\u0939 \u092C\u0921\u093C\u093E \u092C\u0926\u0932\u093E\u0935 \u0939\u0948 \u2014 \u092C\u0947\u0939\u0924\u0930 \u0939\u094B\u0917\u093E \u0915\u094B\u0908 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u0906\u092A\u0915\u094B \u0907\u0938\u092E\u0947\u0902 \u092E\u0926\u0926 \u0915\u0930\u0947\u0964",
    USER_ASKED: "\u091C\u093C\u0930\u0942\u0930 \u2014 \u0915\u093F\u0938\u0940 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u0915\u094B \u091C\u094B\u0921\u093C\u0924\u0947 \u0939\u0948\u0902\u0964",
    ENGINE_UNAVAILABLE: "\u0905\u092D\u0940 \u092E\u0948\u0902 \u0915\u0941\u091B \u0916\u094B\u091C \u0928\u0939\u0940\u0902 \u092A\u093E \u0930\u0939\u093E \u0939\u0942\u0901\u0964 \u0924\u092C \u0924\u0915 \u0915\u094B\u0908 \u0935\u094D\u092F\u0915\u094D\u0924\u093F \u092E\u0926\u0926 \u0915\u0930 \u0938\u0915\u0924\u093E \u0939\u0948\u0964"
  },
  ticket: {
    heading: "\u091F\u093F\u0915\u091F \u092C\u0928\u093E\u090F\u0901",
    subjectLabel: "\u092F\u0939 \u0915\u093F\u0938 \u092C\u093E\u0930\u0947 \u092E\u0947\u0902 \u0939\u0948?",
    detailsLabel: "\u0914\u0930 \u0915\u0941\u091B \u091C\u094B \u092E\u0926\u0926 \u0915\u0930\u0947?",
    detailsHint: "\u0906\u092A\u0915\u0940 \u0905\u092C \u0924\u0915 \u0915\u0940 \u092C\u093E\u0924\u091A\u0940\u0924 \u0938\u093E\u0925 \u092D\u0947\u091C\u0940 \u091C\u093E \u0930\u0939\u0940 \u0939\u0948, \u0926\u094B\u092C\u093E\u0930\u093E \u0932\u093F\u0916\u0928\u0947 \u0915\u0940 \u091C\u093C\u0930\u0942\u0930\u0924 \u0928\u0939\u0940\u0902\u0964",
    submit: "\u0938\u0939\u093E\u092F\u0924\u093E \u0915\u094B \u092D\u0947\u091C\u0947\u0902",
    submitting: "\u092D\u0947\u091C\u093E \u091C\u093E \u0930\u0939\u093E \u0939\u0948\u2026",
    created: "\u091F\u093F\u0915\u091F {number} \u0916\u0941\u0932 \u0917\u092F\u093E \u0939\u0948",
    createdBody: "\u0915\u094B\u0908 \u0907\u0938\u0947 \u0926\u0947\u0916\u0947\u0917\u093E\u0964 \u0906\u092A\u0915\u0947 \u092A\u0902\u091C\u0940\u0915\u0943\u0924 \u0928\u0902\u092C\u0930 \u092A\u0930 \u0905\u092A\u0921\u0947\u091F \u092E\u093F\u0932\u0947\u0917\u093E\u0964",
    failed: "\u092F\u0939 \u092D\u0947\u091C\u093E \u0928\u0939\u0940\u0902 \u0917\u092F\u093E\u0964 \u090F\u0915 \u092C\u093E\u0930 \u0914\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902?"
  }
};
var ta = {
  launcherLabel: "\u0B89\u0BA4\u0BB5\u0BBF \u0BAA\u0BC6\u0BB1\u0BC1\u0B99\u0BCD\u0B95\u0BB3\u0BCD",
  panelTitle: "\u0B89\u0BA4\u0BB5\u0BBF",
  composerPlaceholder: "\u0B92\u0BB0\u0BC1 \u0B95\u0BC7\u0BB3\u0BCD\u0BB5\u0BBF \u0B95\u0BC7\u0BB3\u0BC1\u0B99\u0BCD\u0B95\u0BB3\u0BCD\u2026",
  send: "\u0B85\u0BA9\u0BC1\u0BAA\u0BCD\u0BAA\u0BC1",
  close: "\u0BAE\u0BC2\u0B9F\u0BC1",
  greeting: "\u0BB5\u0BA3\u0B95\u0BCD\u0B95\u0BAE\u0BCD {name} \u{1F44B} \u0BA8\u0BBE\u0BA9\u0BCD {app} \u0B89\u0BA4\u0BB5\u0BBF\u0BAF\u0BBE\u0BB3\u0BB0\u0BCD. {surface} \u0BAA\u0BB1\u0BCD\u0BB1\u0BBF \u0B95\u0BC7\u0BB3\u0BC1\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u2014 \u0B85\u0BB2\u0BCD\u0BB2\u0BA4\u0BC1 \u0B87\u0B99\u0BCD\u0B95\u0BC1\u0BB3\u0BCD\u0BB3 \u0B8E\u0BA4\u0BC8\u0BAF\u0BC1\u0BAE\u0BCD.",
  greetingNoName: "\u0BB5\u0BA3\u0B95\u0BCD\u0B95\u0BAE\u0BCD \u{1F44B} \u0BA8\u0BBE\u0BA9\u0BCD {app} \u0B89\u0BA4\u0BB5\u0BBF\u0BAF\u0BBE\u0BB3\u0BB0\u0BCD. {surface} \u0BAA\u0BB1\u0BCD\u0BB1\u0BBF \u0B95\u0BC7\u0BB3\u0BC1\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u2014 \u0B85\u0BB2\u0BCD\u0BB2\u0BA4\u0BC1 \u0B87\u0B99\u0BCD\u0B95\u0BC1\u0BB3\u0BCD\u0BB3 \u0B8E\u0BA4\u0BC8\u0BAF\u0BC1\u0BAE\u0BCD.",
  answeredLead: "\u0B8E\u0BA9\u0BCD\u0BA9\u0BBF\u0B9F\u0BAE\u0BCD \u0B89\u0BB3\u0BCD\u0BB3\u0BA4\u0BC1 \u0B87\u0BA4\u0BC1\u0BA4\u0BBE\u0BA9\u0BCD:",
  clarifyLead: "\u0B87\u0BB0\u0BA3\u0BCD\u0B9F\u0BC1 \u0BAA\u0BCA\u0BB0\u0BC1\u0BA4\u0BCD\u0BA4\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0B95\u0BBF\u0B9F\u0BC8\u0BA4\u0BCD\u0BA4\u0BA9 \u2014 \u0B8E\u0BA4\u0BC1?",
  helpful: "\u0B87\u0BA4\u0BC1 \u0B89\u0BA4\u0BB5\u0BBF\u0BAF\u0BA4\u0BC1",
  notHelpful: "\u0B9A\u0BB0\u0BBF\u0BAF\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8",
  talkToPerson: "\u0B92\u0BB0\u0BC1\u0BB5\u0BB0\u0BBF\u0B9F\u0BAE\u0BCD \u0BAA\u0BC7\u0B9A\u0BC1\u0B99\u0BCD\u0B95\u0BB3\u0BCD",
  thinking: "\u0BAA\u0BBE\u0BB0\u0BCD\u0B95\u0BCD\u0B95\u0BBF\u0BB1\u0BC7\u0BA9\u0BCD\u2026",
  sourceLabel: "\u0B86\u0BA4\u0BBE\u0BB0\u0BAE\u0BCD",
  localeFallback: "\u0BA4\u0BB1\u0BCD\u0BAA\u0BCB\u0BA4\u0BC1 \u0B86\u0B99\u0BCD\u0B95\u0BBF\u0BB2 \u0BAA\u0BA4\u0BBF\u0BB2\u0BCD\u0B95\u0BB3\u0BCD \u0BAE\u0B9F\u0BCD\u0B9F\u0BC1\u0BAE\u0BC7 \u0B89\u0BB3\u0BCD\u0BB3\u0BA9, \u0B8E\u0BA9\u0BB5\u0BC7 \u0B87\u0BA4\u0BC1 \u0B86\u0B99\u0BCD\u0B95\u0BBF\u0BB2\u0BA4\u0BCD\u0BA4\u0BBF\u0BB2\u0BCD.",
  unavailable: "\u0B87\u0BAA\u0BCD\u0BAA\u0BCB\u0BA4\u0BC1 \u0B89\u0BA4\u0BB5\u0BBF \u0B89\u0BB3\u0BCD\u0BB3\u0B9F\u0B95\u0BCD\u0B95\u0BA4\u0BCD\u0BA4\u0BC8 \u0B85\u0B9F\u0BC8\u0BAF \u0BAE\u0BC1\u0B9F\u0BBF\u0BAF\u0BB5\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8. \u0BA8\u0BC0\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0B9F\u0BBF\u0B95\u0BCD\u0B95\u0BC6\u0B9F\u0BCD \u0B89\u0BB0\u0BC1\u0BB5\u0BBE\u0B95\u0BCD\u0B95\u0BB2\u0BBE\u0BAE\u0BCD, \u0BAF\u0BBE\u0BB0\u0BC7\u0BA9\u0BC1\u0BAE\u0BCD \u0BAA\u0BBE\u0BB0\u0BCD\u0BAA\u0BCD\u0BAA\u0BBE\u0BB0\u0BCD\u0B95\u0BB3\u0BCD.",
  smallTalk: {
    GREETING: "\u0BB5\u0BA3\u0B95\u0BCD\u0B95\u0BAE\u0BCD! \u{1F44B} \u0BA8\u0BBE\u0BA9\u0BCD \u0B8E\u0BAA\u0BCD\u0BAA\u0B9F\u0BBF \u0B89\u0BA4\u0BB5 \u0BAE\u0BC1\u0B9F\u0BBF\u0BAF\u0BC1\u0BAE\u0BCD?",
    THANKS: "\u0BAE\u0B95\u0BBF\u0BB4\u0BCD\u0B9A\u0BCD\u0B9A\u0BBF. \u0BB5\u0BC7\u0BB1\u0BC1 \u0B8F\u0BA4\u0BC7\u0BA9\u0BC1\u0BAE\u0BCD \u0BAA\u0BBE\u0BB0\u0B95\u0BCD\u0B95\u0BB5\u0BBE?",
    GOODBYE: "\u0BAA\u0BA4\u0BCD\u0BA4\u0BBF\u0BB0\u0BAE\u0BBE\u0B95 \u0B87\u0BB0\u0BC1\u0B99\u0BCD\u0B95\u0BB3\u0BCD! \u0BA4\u0BC7\u0BB5\u0BC8\u0BAA\u0BCD\u0BAA\u0B9F\u0BC1\u0BAE\u0BCD\u0BAA\u0BCB\u0BA4\u0BC1 \u0BA8\u0BBE\u0BA9\u0BCD \u0B87\u0B99\u0BCD\u0B95\u0BC7 \u0B87\u0BB0\u0BC1\u0B95\u0BCD\u0B95\u0BBF\u0BB1\u0BC7\u0BA9\u0BCD.",
    AFFIRM: "\u0BB5\u0BC7\u0BB1\u0BC1 \u0B8F\u0BA4\u0BC7\u0BA9\u0BC1\u0BAE\u0BCD \u0B89\u0BA4\u0BB5\u0BBF \u0BB5\u0BC7\u0BA3\u0BCD\u0B9F\u0BC1\u0BAE\u0BBE?",
    HOW_ARE_YOU: "\u0BA8\u0BA9\u0BCD\u0BB1\u0BBE\u0B95 \u0B87\u0BB0\u0BC1\u0B95\u0BCD\u0B95\u0BBF\u0BB1\u0BC7\u0BA9\u0BCD! \u0B8E\u0BA9\u0BCD\u0BA9 \u0B89\u0BA4\u0BB5\u0BBF \u0BB5\u0BC7\u0BA3\u0BCD\u0B9F\u0BC1\u0BAE\u0BCD?",
    IDENTITY: "\u0BA8\u0BBE\u0BA9\u0BCD {app} \u0B89\u0BA4\u0BB5\u0BBF\u0BAF\u0BBE\u0BB3\u0BB0\u0BCD \u2014 {app} \u0B87\u0BA9\u0BCD \u0B89\u0BA4\u0BB5\u0BBF\u0B95\u0BCD \u0B95\u0B9F\u0BCD\u0B9F\u0BC1\u0BB0\u0BC8\u0B95\u0BB3\u0BBF\u0BB2\u0BBF\u0BB0\u0BC1\u0BA8\u0BCD\u0BA4\u0BC1 \u0BAA\u0BA4\u0BBF\u0BB2\u0BB3\u0BBF\u0B95\u0BCD\u0B95\u0BC1\u0BAE\u0BCD \u0BA4\u0BBE\u0BA9\u0BBF\u0BAF\u0B99\u0BCD\u0B95\u0BBF \u0B89\u0BA4\u0BB5\u0BBF\u0BAF\u0BBE\u0BB3\u0BB0\u0BCD.",
    HELP_OFFER: "\u0B95\u0BA3\u0BCD\u0B9F\u0BBF\u0BAA\u0BCD\u0BAA\u0BBE\u0B95 \u2014 \u0B8E\u0BA4\u0BBF\u0BB2\u0BCD \u0B89\u0BA4\u0BB5\u0BBF \u0BB5\u0BC7\u0BA3\u0BCD\u0B9F\u0BC1\u0BAE\u0BCD?",
    UNCLEAR: "\u0BAE\u0BA9\u0BCD\u0BA9\u0BBF\u0B95\u0BCD\u0B95\u0BB5\u0BC1\u0BAE\u0BCD, \u0BAA\u0BC1\u0BB0\u0BBF\u0BAF\u0BB5\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8. \u0BA8\u0BBE\u0BA9\u0BCD \u0B8E\u0BAA\u0BCD\u0BAA\u0B9F\u0BBF \u0B89\u0BA4\u0BB5 \u0BAE\u0BC1\u0B9F\u0BBF\u0BAF\u0BC1\u0BAE\u0BCD?"
  },
  escalate: {
    NO_MATCH: "\u0B87\u0BA4\u0BB1\u0BCD\u0B95\u0BC1 \u0BA8\u0BB2\u0BCD\u0BB2 \u0BAA\u0BA4\u0BBF\u0BB2\u0BCD \u0B8E\u0BA9\u0BCD\u0BA9\u0BBF\u0B9F\u0BAE\u0BCD \u0B87\u0BB2\u0BCD\u0BB2\u0BC8. \u0B92\u0BB0\u0BC1\u0BB5\u0BB0\u0BC8 \u0B87\u0BA3\u0BC8\u0B95\u0BCD\u0B95\u0BBF\u0BB1\u0BC7\u0BA9\u0BCD.",
    ACCOUNT_SPECIFIC: "\u0B87\u0BA4\u0BC1 \u0B89\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0B9A\u0BCA\u0BA8\u0BCD\u0BA4\u0B95\u0BCD \u0B95\u0BA3\u0B95\u0BCD\u0B95\u0BC1 \u0BAA\u0BB1\u0BCD\u0BB1\u0BBF\u0BAF\u0BA4\u0BC1, \u0B89\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0BB5\u0BBF\u0BB5\u0BB0\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0B8E\u0BA9\u0B95\u0BCD\u0B95\u0BC1\u0BA4\u0BCD \u0BA4\u0BC6\u0BB0\u0BBF\u0BAF\u0BBE\u0BA4\u0BC1. \u0B92\u0BB0\u0BC1\u0BB5\u0BB0\u0BBE\u0BB2\u0BCD \u0BAA\u0BBE\u0BB0\u0BCD\u0B95\u0BCD\u0B95 \u0BAE\u0BC1\u0B9F\u0BBF\u0BAF\u0BC1\u0BAE\u0BCD.",
    MONEY_MOVEMENT: "\u0BAA\u0BA3\u0BAE\u0BCD \u0BA4\u0BCA\u0B9F\u0BB0\u0BCD\u0BAA\u0BBE\u0BA9 \u0B8E\u0BA4\u0BC1\u0BB5\u0BC1\u0BAE\u0BCD \u0BA8\u0BC7\u0BB0\u0B9F\u0BBF\u0BAF\u0BBE\u0B95 \u0B92\u0BB0\u0BC1\u0BB5\u0BB0\u0BBF\u0B9F\u0BAE\u0BCD \u0B9A\u0BC6\u0BB2\u0BCD\u0BB2 \u0BB5\u0BC7\u0BA3\u0BCD\u0B9F\u0BC1\u0BAE\u0BCD. \u0B85\u0BA4\u0BC8\u0B9A\u0BCD \u0B9A\u0BC6\u0BAF\u0BCD\u0BB5\u0BCB\u0BAE\u0BCD.",
    REGULATORY: "\u0B87\u0BA4\u0BB1\u0BCD\u0B95\u0BC1 \u0B89\u0B9F\u0BA9\u0B9F\u0BBF\u0BAF\u0BBE\u0B95 \u0B92\u0BB0\u0BC1\u0BB5\u0BB0\u0BCD \u0BA4\u0BC7\u0BB5\u0BC8. \u0B85\u0BB5\u0B9A\u0BB0\u0BAE\u0BBE\u0B95\u0BAA\u0BCD \u0BAA\u0BA4\u0BBF\u0BB5\u0BC1 \u0B9A\u0BC6\u0BAF\u0BCD\u0B95\u0BBF\u0BB1\u0BC7\u0BA9\u0BCD.",
    DESTRUCTIVE: "\u0B87\u0BA4\u0BC1 \u0BAA\u0BC6\u0BB0\u0BBF\u0BAF \u0BAE\u0BBE\u0BB1\u0BCD\u0BB1\u0BAE\u0BCD \u2014 \u0B92\u0BB0\u0BC1\u0BB5\u0BB0\u0BCD \u0B89\u0B99\u0BCD\u0B95\u0BB3\u0BC1\u0B95\u0BCD\u0B95\u0BC1 \u0B89\u0BA4\u0BB5\u0BC1\u0BB5\u0BA4\u0BC1 \u0BA8\u0BB2\u0BCD\u0BB2\u0BA4\u0BC1.",
    USER_ASKED: "\u0B95\u0BA3\u0BCD\u0B9F\u0BBF\u0BAA\u0BCD\u0BAA\u0BBE\u0B95 \u2014 \u0B92\u0BB0\u0BC1\u0BB5\u0BB0\u0BC8 \u0B87\u0BA3\u0BC8\u0B95\u0BCD\u0B95\u0BBF\u0BB1\u0BC7\u0BA9\u0BCD.",
    ENGINE_UNAVAILABLE: "\u0B87\u0BAA\u0BCD\u0BAA\u0BCB\u0BA4\u0BC1 \u0B8E\u0BA9\u0BCD\u0BA9\u0BBE\u0BB2\u0BCD \u0BA4\u0BC7\u0B9F \u0BAE\u0BC1\u0B9F\u0BBF\u0BAF\u0BB5\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8. \u0B85\u0BA4\u0BC1\u0BB5\u0BB0\u0BC8 \u0B92\u0BB0\u0BC1\u0BB5\u0BB0\u0BCD \u0B89\u0BA4\u0BB5 \u0BAE\u0BC1\u0B9F\u0BBF\u0BAF\u0BC1\u0BAE\u0BCD."
  },
  ticket: {
    heading: "\u0B9F\u0BBF\u0B95\u0BCD\u0B95\u0BC6\u0B9F\u0BCD \u0B89\u0BB0\u0BC1\u0BB5\u0BBE\u0B95\u0BCD\u0B95\u0BC1",
    subjectLabel: "\u0B87\u0BA4\u0BC1 \u0B8E\u0BA4\u0BC8\u0BAA\u0BCD \u0BAA\u0BB1\u0BCD\u0BB1\u0BBF\u0BAF\u0BA4\u0BC1?",
    detailsLabel: "\u0B89\u0BA4\u0BB5\u0BC1\u0BAE\u0BCD \u0BB5\u0BC7\u0BB1\u0BC1 \u0B8F\u0BA4\u0BC7\u0BA9\u0BC1\u0BAE\u0BCD?",
    detailsHint: "\u0B89\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0B89\u0BB0\u0BC8\u0BAF\u0BBE\u0B9F\u0BB2\u0BCD \u0B87\u0BA3\u0BC8\u0B95\u0BCD\u0B95\u0BAA\u0BCD\u0BAA\u0B9F\u0BC1\u0B95\u0BBF\u0BB1\u0BA4\u0BC1, \u0BAE\u0BC0\u0BA3\u0BCD\u0B9F\u0BC1\u0BAE\u0BCD \u0B8E\u0BB4\u0BC1\u0BA4 \u0BB5\u0BC7\u0BA3\u0BCD\u0B9F\u0BBF\u0BAF\u0BA4\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8.",
    submit: "\u0B89\u0BA4\u0BB5\u0BBF\u0B95\u0BCD\u0B95\u0BC1 \u0B85\u0BA9\u0BC1\u0BAA\u0BCD\u0BAA\u0BC1",
    submitting: "\u0B85\u0BA9\u0BC1\u0BAA\u0BCD\u0BAA\u0BC1\u0B95\u0BBF\u0BB1\u0BA4\u0BC1\u2026",
    created: "\u0B9F\u0BBF\u0B95\u0BCD\u0B95\u0BC6\u0B9F\u0BCD {number} \u0BA4\u0BBF\u0BB1\u0B95\u0BCD\u0B95\u0BAA\u0BCD\u0BAA\u0B9F\u0BCD\u0B9F\u0BA4\u0BC1",
    createdBody: "\u0BAF\u0BBE\u0BB0\u0BC7\u0BA9\u0BC1\u0BAE\u0BCD \u0BAA\u0BBE\u0BB0\u0BCD\u0BAA\u0BCD\u0BAA\u0BBE\u0BB0\u0BCD\u0B95\u0BB3\u0BCD. \u0B89\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0BAA\u0BA4\u0BBF\u0BB5\u0BC1 \u0B8E\u0BA3\u0BCD\u0BA3\u0BC1\u0B95\u0BCD\u0B95\u0BC1\u0BA4\u0BCD \u0BA4\u0B95\u0BB5\u0BB2\u0BCD \u0BB5\u0BB0\u0BC1\u0BAE\u0BCD.",
    failed: "\u0B87\u0BA4\u0BC1 \u0B85\u0BA9\u0BC1\u0BAA\u0BCD\u0BAA\u0BAA\u0BCD\u0BAA\u0B9F\u0BB5\u0BBF\u0BB2\u0BCD\u0BB2\u0BC8. \u0BAE\u0BC0\u0BA3\u0BCD\u0B9F\u0BC1\u0BAE\u0BCD \u0BAE\u0BC1\u0BAF\u0BB1\u0BCD\u0B9A\u0BBF\u0B95\u0BCD\u0B95\u0BB5\u0BBE?"
  }
};
var CATALOGUE = { en, hi, ta };
function stringsFor(locale) {
  return CATALOGUE[locale] ?? CATALOGUE[locale.split("-")[0] ?? ""] ?? en;
}
function format(template, values) {
  return template.replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);
}

// src/engine/corpus-engine.ts
var INTENT_TO_REASON = {
  ACCOUNT_SPECIFIC: "ACCOUNT_SPECIFIC",
  MONEY_MOVEMENT: "MONEY_MOVEMENT",
  REGULATORY: "REGULATORY",
  DESTRUCTIVE: "DESTRUCTIVE"
};
var CorpusEngine = class {
  tier;
  retriever;
  embedder;
  routeHint;
  warming = null;
  constructor(config) {
    this.retriever = new Retriever(config.corpus);
    this.embedder = config.embedder ?? null;
    this.routeHint = config.routeHint ?? null;
    this.tier = this.embedder ? "semantic" : "lexical";
  }
  /**
   * Embeds any chunk the bundle shipped without a vector — that is, the tenant
   * delta. Idempotent and concurrency-safe: a second caller awaits the first.
   *
   * A failure here is deliberately swallowed. The delta staying lexical-only is a
   * quality regression on a handful of articles; throwing would take the whole
   * semantic tier down with it for a corpus that is already 95% vectorised.
   */
  warm() {
    if (!this.warming) this.warming = this.doWarm();
    return this.warming;
  }
  async doWarm() {
    if (!this.embedder) return;
    const pending = this.retriever.unvectorisedChunks();
    if (!pending.length) return;
    try {
      const vectors = await this.embedder.embed(pending.map((chunk) => chunk.text));
      const dim = this.embedder.dim;
      const packed = new Float32Array(vectors.length * dim);
      vectors.forEach((vector, index) => packed.set(normalise(vector), index * dim));
      this.retriever.addBank({
        vectors: packed,
        chunkOf: pending.map((chunk) => chunk.index)
      });
    } catch {
    }
  }
  async *answer(turn, signal) {
    const strings = stringsFor(turn.locale);
    const intent = classifyIntent(turn.question);
    if (intent) {
      yield {
        kind: "verdict",
        verdict: { status: "escalate", reason: INTENT_TO_REASON[intent.code] }
      };
      const route = this.routeHint?.(intent.code, turn.activeSurface) ?? null;
      if (route) yield { kind: "route", label: route.label, path: route.path };
      return;
    }
    const smallTalk = classifySmallTalk(turn.question);
    if (smallTalk) {
      yield { kind: "verdict", verdict: { status: "smalltalk", code: smallTalk } };
      yield { kind: "delta", text: strings.smallTalk[smallTalk] };
      return;
    }
    if (tokenizeRaw(turn.question).length === 0) {
      yield { kind: "verdict", verdict: { status: "smalltalk", code: "UNCLEAR" } };
      yield { kind: "delta", text: strings.smallTalk.UNCLEAR };
      return;
    }
    let queryVector = null;
    if (this.embedder && this.retriever.hasVectors) {
      try {
        const [vector] = await this.embedder.embed([turn.question]);
        if (vector) queryVector = normalise(vector);
      } catch {
      }
    }
    if (signal.aborted) return;
    const passages = this.retriever.search(turn.question, turn.activeSurface, queryVector);
    const scoredTier = queryVector ? "semantic" : "lexical";
    const verdict = verdictFor(scoredTier, passages);
    yield { kind: "verdict", verdict };
    if (verdict.status !== "answered") return;
    yield { kind: "delta", text: composeAnswer(passages, strings.answeredLead) };
    yield { kind: "citations", passages: dedupeByArticle(passages) };
  }
  dispose() {
    this.embedder?.dispose();
  }
};
function composeAnswer(passages, lead) {
  const top = passages[0];
  if (!top) return lead;
  return `${lead}

${top.text}`;
}
function dedupeByArticle(passages) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const passage of passages) {
    if (seen.has(passage.articleId)) continue;
    seen.add(passage.articleId);
    out.push(passage);
  }
  return out;
}
var NullEngine = class {
  tier = "none";
  async warm() {
  }
  async *answer() {
    yield { kind: "verdict", verdict: { status: "escalate", reason: "ENGINE_UNAVAILABLE" } };
  }
  dispose() {
  }
};

// src/engine/select.ts
var MIN_DEVICE_MEMORY_GB = 2;
var METERED_EFFECTIVE_TYPES = /* @__PURE__ */ new Set(["slow-2g", "2g"]);
function probeCapabilities() {
  if (typeof navigator === "undefined") {
    return { workers: false, saveData: false, metered: false, deviceMemoryGb: null };
  }
  const connection = navigator.connection;
  const deviceMemory = navigator.deviceMemory;
  return {
    workers: typeof Worker !== "undefined",
    saveData: connection?.saveData === true,
    metered: METERED_EFFECTIVE_TYPES.has(connection?.effectiveType ?? ""),
    // Chromium-only. `null` is "unknown", and unknown must not block the tier —
    // Safari and Firefox report nothing at all, and treating that as a small device
    // would demote every non-Chromium desktop.
    deviceMemoryGb: typeof deviceMemory === "number" ? deviceMemory : null
  };
}
function resolveTier(preferred, capabilities) {
  if (preferred === "lexical") return { tier: "lexical" };
  if (!capabilities.workers) return { tier: "lexical", downgradedBecause: "no-workers" };
  if (capabilities.saveData) return { tier: "lexical", downgradedBecause: "save-data" };
  if (capabilities.metered) return { tier: "lexical", downgradedBecause: "metered" };
  if (capabilities.deviceMemoryGb !== null && capabilities.deviceMemoryGb < MIN_DEVICE_MEMORY_GB) {
    return { tier: "lexical", downgradedBecause: "low-memory" };
  }
  return { tier: "semantic" };
}
async function pickDevice() {
  try {
    const gpu = navigator.gpu;
    if (!gpu) return "wasm";
    return await gpu.requestAdapter() ? "webgpu" : "wasm";
  } catch {
    return "wasm";
  }
}

// src/engine/worker/protocol.ts
var DEFAULT_EMBED_MODEL = "Xenova/all-MiniLM-L6-v2";
var DEFAULT_EMBED_DIM = 384;

// src/engine/worker/client.ts
var INIT_TIMEOUT_MS = 6e4;
var EMBED_TIMEOUT_MS = 2e4;
var EmbedClient = class {
  worker = null;
  pending = /* @__PURE__ */ new Map();
  nextId = 1;
  disposed = false;
  /** Set once `init` resolves. */
  dim = 0;
  device = null;
  async init(device, model = DEFAULT_EMBED_MODEL) {
    if (this.disposed) throw new Error("EmbedClient disposed");
    this.worker = new Worker(new URL("./embed.worker.js", import.meta.url), {
      type: "module"
    });
    this.worker.onmessage = (event) => {
      const entry = this.pending.get(event.data.id);
      if (!entry) return;
      clearTimeout(entry.timer);
      this.pending.delete(event.data.id);
      if (event.data.type === "error") entry.reject(new Error(event.data.message));
      else entry.resolve(event.data);
    };
    this.worker.onerror = (event) => {
      const error = new Error(event.message || "embedding worker failed to load");
      for (const [, entry] of this.pending) {
        clearTimeout(entry.timer);
        entry.reject(error);
      }
      this.pending.clear();
    };
    const response = await this.send(
      { type: "init", model, device },
      INIT_TIMEOUT_MS
    );
    if (response.type !== "ready") throw new Error("unexpected init response");
    this.dim = response.dim;
    this.device = response.device;
  }
  /**
   * Embeds a batch, returning one `Float32Array` view per input.
   *
   * The views share the transferred buffer rather than copying: the caller either
   * packs them straight into a bank or uses one for a single query, and neither
   * outlives the buffer.
   */
  async embed(texts) {
    if (!texts.length) return [];
    const response = await this.send({ type: "embed", texts }, EMBED_TIMEOUT_MS);
    if (response.type !== "embedded") throw new Error("unexpected embed response");
    const all = new Float32Array(response.vectors);
    const out = [];
    for (let i = 0; i < response.count; i++) {
      out.push(all.subarray(i * response.dim, (i + 1) * response.dim));
    }
    return out;
  }
  dispose() {
    this.disposed = true;
    for (const [, entry] of this.pending) {
      clearTimeout(entry.timer);
      entry.reject(new Error("EmbedClient disposed"));
    }
    this.pending.clear();
    this.worker?.terminate();
    this.worker = null;
  }
  send(request, timeoutMs) {
    const worker = this.worker;
    if (!worker) return Promise.reject(new Error("worker not started"));
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`embedding worker timed out after ${timeoutMs}ms`));
      }, timeoutMs);
      this.pending.set(id, { resolve, reject, timer });
      worker.postMessage({ ...request, id });
    });
  }
};
var DEFAULT_ASSET_BASE = "/assistant/";
var CorpusUnavailableError = class extends Error {
  constructor(message, options) {
    super(message, options);
    this.name = "CorpusUnavailableError";
  }
};
async function getJson(url, signal) {
  const response = await fetch(url, signal ? { signal } : void 0);
  if (!response.ok) {
    throw new CorpusUnavailableError(`${url} responded ${response.status}`);
  }
  return await response.json();
}
async function loadBakedCorpus(options) {
  const base = options.assetBase ?? DEFAULT_ASSET_BASE;
  const manifest = await getJson(`${base}manifest.json`, options.signal);
  const entry = manifest.locales[options.locale] ?? (options.fallbackLocale ? manifest.locales[options.fallbackLocale] : void 0);
  if (!entry) {
    throw new CorpusUnavailableError(
      `no bundle for locale "${options.locale}" (or its fallback) in ${base}manifest.json`
    );
  }
  const bundle = await getJson(`${base}${entry.bundle}`, options.signal);
  if (bundle.schema !== 1) {
    throw new CorpusUnavailableError(`bundle schema ${String(bundle.schema)} is not supported`);
  }
  let vectors = null;
  const dim = bundle.embedder?.dim ?? 0;
  if (entry.vectors && bundle.embedder) {
    try {
      const response = await fetch(
        `${base}${entry.vectors}`,
        options.signal ? { signal: options.signal } : void 0
      );
      if (!response.ok) throw new Error(`${response.status}`);
      vectors = unpackVectors(new Uint8Array(await response.arrayBuffer()), dim);
    } catch (cause) {
      if (cause?.name === "AbortError") throw cause;
      vectors = null;
    }
  }
  return { bundle, vectors, dim };
}
async function mergeTenantDelta(base, options = {}) {
  const page = await fetchPublishedFaq({
    ...options.surface ? { surface: options.surface } : {},
    size: 100,
    ...options.signal ? { signal: options.signal } : {}
  });
  const naturalKey = (surfaceKey, title) => `${surfaceKey}\0${title}`;
  const known = new Set(
    base.bundle.articles.map((article) => naturalKey(article.surfaceKey, article.title))
  );
  const articles = [...base.bundle.articles];
  const chunks = [...base.bundle.chunks];
  for (const article of page.items) {
    if (known.has(naturalKey(article.surfaceKey, article.title))) continue;
    const index = articles.length;
    articles.push({
      id: article.id,
      title: article.title,
      surfaceKey: article.surfaceKey,
      type: "FAQ",
      locale: article.locale,
      updatedAt: article.updatedAt
    });
    for (const chunk of chunkArticle({ title: article.title, body: article.body })) {
      chunks.push({ a: index, t: chunk.title, x: chunk.text, v: -1 });
    }
  }
  if (articles.length === base.bundle.articles.length) return base;
  return {
    ...base,
    bundle: { ...base.bundle, articles, chunks }
  };
}
var MAX_DESCRIPTION = 2e3;
var MAX_SUBJECT = 140;
var SOURCE_APP_BY_SURFACE = {
  admin: "admin-portal",
  campaign: "campaign-console",
  commerce: "commerce-console",
  compliance: "compliance-portal",
  crm: "crm-console",
  ledger: "ledger-console",
  loans: "loans-desk",
  operations: "operations-console",
  partner: "partner-portal",
  portfolio: "portfolio-app",
  pos: "pos-terminal",
  pwa: "customer-pwa"
};
var DEFAULT_SOURCE_APP = "quickbee-app";
function sourceAppFor(surface) {
  return surface && SOURCE_APP_BY_SURFACE[surface] || DEFAULT_SOURCE_APP;
}
function priorityFor(reason) {
  switch (reason) {
    case "REGULATORY":
      return priorityForIntent("REGULATORY");
    case "MONEY_MOVEMENT":
      return priorityForIntent("MONEY_MOVEMENT");
    default:
      return "MEDIUM";
  }
}
function renderTranscript(messages) {
  return messages.map((message) => `${message.role === "user" ? "Customer" : "Assistant"}: ${message.text}`).join("\n");
}
function renderContext(context) {
  const lines = [
    "\u2014 Assistant context \u2014",
    `Reason: ${context.reason}`,
    `Tier: ${context.tier}`,
    `App: ${context.activeSurface ?? "n/a"}${context.route ? ` \xB7 Route: ${context.route}` : ""}`
  ];
  if (context.nearMisses?.length) {
    lines.push(
      "Closest articles: " + context.nearMisses.slice(0, 3).map((passage) => `"${passage.title}" (${passage.score.toFixed(2)})`).join(", ")
    );
  }
  return lines.join("\n");
}
function buildDescription(args) {
  const context = renderContext(args.context);
  const details = args.details?.trim();
  const head = details ? `${details}

` : "";
  const FULL_HEADER = "\u2014 Conversation \u2014\n";
  const TRIMMED_HEADER = "\u2014 Conversation (earlier turns omitted) \u2014\n";
  const fixed = `${head}${context}`;
  if (fixed.length >= MAX_DESCRIPTION) return fixed.slice(0, MAX_DESCRIPTION);
  const transcript = renderTranscript(args.messages);
  const compose = (header, body) => `${head}${header}${body}

${context}`;
  const whole = compose(FULL_HEADER, transcript);
  if (whole.length <= MAX_DESCRIPTION) return whole;
  const budget = MAX_DESCRIPTION - compose(TRIMMED_HEADER, "").length;
  if (budget <= 0) return fixed;
  const kept = transcript.slice(transcript.length - budget);
  const firstBreak = kept.indexOf("\n");
  const trimmed = firstBreak === -1 ? "" : kept.slice(firstBreak + 1);
  return trimmed ? compose(TRIMMED_HEADER, trimmed) : fixed;
}
async function raiseTicket(args) {
  return createSupportTicket({
    ...args.customerName ? { customerName: args.customerName } : {},
    subject: args.subject.trim().slice(0, MAX_SUBJECT) || "Support request",
    description: buildDescription(args),
    channel: "QUICKBEE_APP",
    priority: priorityFor(args.context.reason),
    sourceApp: sourceAppFor(args.context.activeSurface),
    draftId: args.conversationId
  });
}

// src/react/useAssistant.ts
var messageSequence = 0;
function nextId() {
  messageSequence += 1;
  return `m${messageSequence}`;
}
function useAssistant(options) {
  const {
    preferredTier,
    locale,
    activeSurface,
    appName,
    surfaceLabel,
    userName,
    assetBase,
    routeHint,
    currentPath,
    onEvent
  } = options;
  const strings = useMemo(() => stringsFor(locale), [locale]);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [tier, setTier] = useState("none");
  const [ticket, setTicket] = useState({ status: "idle" });
  const engineRef = useRef(null);
  const bootRef = useRef(null);
  const abortRef = useRef(null);
  const conversationRef = useRef(crypto.randomUUID());
  const localeRef = useRef(locale);
  const surfaceRef = useRef(activeSurface);
  localeRef.current = locale;
  surfaceRef.current = activeSurface;
  const emit = useCallback(
    (event) => {
      onEvent?.(event);
    },
    [onEvent]
  );
  const bootEngine = useCallback(async () => {
    const decision = resolveTier(preferredTier, probeCapabilities());
    let corpus;
    try {
      corpus = await loadBakedCorpus({
        locale: localeRef.current,
        fallbackLocale: "en",
        ...assetBase ? { assetBase } : {}
      });
    } catch {
      const engine2 = new NullEngine();
      setTier("none");
      emit({ type: "engine-ready", tier: "none" });
      return engine2;
    }
    try {
      corpus = await mergeTenantDelta(corpus, {
        ...surfaceRef.current ? { surface: surfaceRef.current } : {}
      });
    } catch {
    }
    let embedder = null;
    if (decision.tier === "semantic" && corpus.vectors) {
      embedder = new EmbedClient();
      try {
        await embedder.init(await pickDevice());
      } catch {
        embedder.dispose();
        embedder = null;
      }
    }
    const engine = new CorpusEngine({
      corpus,
      embedder,
      ...routeHint ? { routeHint } : {}
    });
    void engine.warm();
    setTier(engine.tier);
    emit({
      type: "engine-ready",
      tier: engine.tier,
      ...decision.downgradedBecause ? { downgradedBecause: decision.downgradedBecause } : {}
    });
    return engine;
  }, [preferredTier, assetBase, routeHint, emit]);
  const ensureEngine = useCallback(async () => {
    if (engineRef.current) return engineRef.current;
    if (!bootRef.current) {
      bootRef.current = bootEngine().then((engine) => {
        engineRef.current = engine;
        return engine;
      });
    }
    return bootRef.current;
  }, [bootEngine]);
  const openPanel = useCallback(() => {
    setOpen(true);
    emit({ type: "opened" });
    setMessages((current) => {
      if (current.length) return current;
      const template = userName ? strings.greeting : strings.greetingNoName;
      return [
        {
          id: nextId(),
          role: "assistant",
          text: format(template, {
            name: userName ?? "",
            app: appName,
            surface: surfaceLabel
          })
        }
      ];
    });
    void ensureEngine();
  }, [emit, ensureEngine, userName, strings, appName, surfaceLabel]);
  const closePanel = useCallback(() => {
    setOpen(false);
    abortRef.current?.abort();
  }, []);
  const ask = useCallback(
    async (question) => {
      const text = question.trim();
      if (!text || thinking) return;
      const pendingId = nextId();
      setMessages((current) => [
        ...current,
        { id: nextId(), role: "user", text },
        { id: pendingId, role: "assistant", text: strings.thinking, pending: true }
      ]);
      setThinking(true);
      setTicket({ status: "idle" });
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const engine = await ensureEngine();
      const reply = { id: pendingId, role: "assistant", text: "" };
      let outcome = "escalate";
      try {
        for await (const chunk of engine.answer(
          { question: text, activeSurface, locale },
          controller.signal
        )) {
          if (chunk.kind === "verdict") {
            const { verdict } = chunk;
            outcome = verdict.status;
            if (verdict.status === "smalltalk") {
              reply.text = "";
            } else if (verdict.status === "clarify") {
              reply.text = strings.clarifyLead;
              reply.suggestions = verdict.suggestions;
            } else if (verdict.status === "escalate") {
              reply.text = engine.tier === "none" ? strings.unavailable : strings.escalate[verdict.reason];
              reply.escalation = { reason: verdict.reason, nearMisses: [] };
            }
          } else if (chunk.kind === "delta") {
            reply.text += format(chunk.text, { app: appName });
          } else if (chunk.kind === "citations") {
            reply.passages = chunk.passages;
            if (reply.escalation) reply.escalation.nearMisses = chunk.passages;
          } else {
            reply.route = { label: chunk.label, path: chunk.path };
          }
        }
      } catch {
        reply.text = strings.unavailable;
        reply.escalation = { reason: "ENGINE_UNAVAILABLE", nearMisses: [] };
        outcome = "escalate";
      }
      if (controller.signal.aborted) return;
      setMessages(
        (current) => current.map((message) => message.id === pendingId ? reply : message)
      );
      setThinking(false);
      emit({ type: "asked", verdict: outcome, tier: engine.tier });
    },
    [thinking, ensureEngine, activeSurface, locale, strings, appName, emit]
  );
  const requestHuman = useCallback(() => {
    setMessages((current) => [
      ...current,
      {
        id: nextId(),
        role: "assistant",
        text: strings.escalate.USER_ASKED,
        escalation: { reason: "USER_ASKED", nearMisses: lastPassages(current) }
      }
    ]);
  }, [strings]);
  const submitTicket = useCallback(
    async (input) => {
      if (!input.escalation) return;
      setTicket({ status: "sending" });
      const transcript = messages.filter((message) => !message.pending).map((message) => ({ role: message.role, text: message.text }));
      try {
        const created = await raiseTicket({
          conversationId: conversationRef.current,
          subject: input.subject,
          details: input.details,
          messages: transcript,
          ...userName ? { customerName: userName } : {},
          context: {
            reason: input.escalation.reason,
            tier,
            activeSurface,
            ...currentPath ? { route: currentPath } : {},
            nearMisses: input.escalation.nearMisses
          }
        });
        setTicket({ status: "sent", ticketNumber: created.ticketNumberDisplay });
        emit({ type: "ticket-created", ticketNumber: created.ticketNumberDisplay });
      } catch {
        setTicket({ status: "failed" });
        emit({ type: "ticket-failed" });
      }
    },
    [messages, userName, tier, activeSurface, currentPath, emit]
  );
  useEffect(
    () => () => {
      abortRef.current?.abort();
      engineRef.current?.dispose();
    },
    []
  );
  return {
    open,
    openPanel,
    closePanel,
    messages,
    thinking,
    tier,
    strings,
    ticket,
    ask,
    requestHuman,
    submitTicket
  };
}
function lastPassages(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const passages = messages[i]?.passages;
    if (passages?.length) return passages;
  }
  return [];
}
function AssistantHost({
  enabled = true,
  variant = "desktop",
  ...options
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    ensureAssistantStyles();
    setMounted(true);
  }, []);
  const assistant = useAssistant({
    preferredTier: options.preferredTier,
    locale: options.locale,
    activeSurface: options.activeSurface,
    appName: options.appName,
    surfaceLabel: options.surfaceLabel,
    ...options.userName !== void 0 ? { userName: options.userName } : {},
    ...options.assetBase ? { assetBase: options.assetBase } : {},
    ...options.routeHint ? { routeHint: options.routeHint } : {},
    ...options.currentPath ? { currentPath: options.currentPath } : {},
    ...options.onEvent ? { onEvent: options.onEvent } : {}
  });
  useEffect(() => {
    if (!assistant.open) return void 0;
    const onKeyDown = (event) => {
      if (event.key === "Escape") assistant.closePanel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [assistant.open, assistant.closePanel]);
  if (!enabled || !mounted) return null;
  return createPortal(
    assistant.open ? /* @__PURE__ */ jsx(
      AssistantPanel,
      {
        strings: assistant.strings,
        messages: assistant.messages,
        thinking: assistant.thinking,
        ticket: assistant.ticket,
        variant,
        onClose: assistant.closePanel,
        onAsk: assistant.ask,
        onRequestHuman: assistant.requestHuman,
        onSubmitTicket: assistant.submitTicket,
        ...options.onNavigate ? { onNavigate: options.onNavigate } : {}
      }
    ) : /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: [cx.scope, cx.launcher, variant === "mobile" ? cx.launcherMobile : ""].filter(Boolean).join(" "),
        onClick: assistant.openPanel,
        "aria-label": assistant.strings.launcherLabel,
        "data-testid": "qb-assistant-launcher",
        children: /* @__PURE__ */ jsx("svg", { width: "22", height: "22", viewBox: "0 0 24 24", "aria-hidden": "true", children: /* @__PURE__ */ jsx(
          "path",
          {
            d: "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "1.8",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ) })
      }
    ),
    document.body
  );
}

export { ASSISTANT_CSS, AssistantHost, BANDS, CorpusEngine, CorpusUnavailableError, DEFAULT_EMBED_DIM, DEFAULT_EMBED_MODEL, NullEngine, Retriever, SOURCE_APP_BY_SURFACE, buildBm25Index, buildDescription, chunkArticle, chunkSearchText, classifyIntent, composeAnswer, ensureAssistantStyles, format, fuse, loadBakedCorpus, mergeTenantDelta, normalise, normaliseBm25, packVectors, pickDevice, priorityForIntent, probeCapabilities, quantiseInt8, raiseTicket, renderTranscript, resolveTier, scoreBm25, sourceAppFor, stringsFor, tokenize, unpackVectors, useAssistant, verdictFor };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map